import { useState } from 'react';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { RecordActions } from '../../components/RecordActions';
import { RECORD_FIELDS } from '../../components/recordFields';
import { KeyValue, Pill, Spinner, FormGrid, Field, TextInput, NumberInput, DateInput, TextArea, SelectInput, LineTable } from '../../components/ui';
import { SearchSelect } from '../../components/SearchSelect';
import { useDetail } from '../../lib/useDetail';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { dateLabel, num, customerSuppliedMaterialStatus, customerSuppliedScenario, customerMaterialReturnHandling } from '../../lib/format';
import type {
  CustomerSuppliedMaterial, CustomerMaterialReturn, CustomerMaterialReturnLine,
  Customer, Item, WorkOrder,
} from '../../lib/types';

const today = () => new Date().toISOString().slice(0, 10);

// 可推进的状态（归还必须走交还单，故不含 returned）。
const NEXT_STATUS: Record<string, { to: string; label: string }[]> = {
  received: [{ to: 'in_process', label: '开始维修' }, { to: 'awaiting_return', label: '判定无法修复/待退回' }],
  in_process: [{ to: 'awaiting_return', label: '维修完成/待交还' }],
  awaiting_return: [{ to: 'in_process', label: '返修/继续维修' }],
};

// —— 来料详情：关键字段 + 状态推进 + 开具交还单（闭环「来料 → 工单 → 交还」）——
function MaterialDetail({ row, onChanged }: { row: CustomerSuppliedMaterial; onChanged: () => void }) {
  const { user } = useAuth();
  const canWrite = can(user, 'customer-supplied-materials', 'create');
  const { data, loading, error, reload } = useDetail<CustomerSuppliedMaterial>(`/api/customer-supplied-materials/${row.id}`);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [returnOpen, setReturnOpen] = useState(false);
  const m = data ?? row;
  const terminal = m.status === 'returned' || m.status === 'scrapped';

  const transition = async (to: string) => {
    setErr(null); setOk(null); setBusy(true);
    try {
      await api.post(`/api/customer-supplied-materials/${m.id}/status`, { status: to });
      reload(); onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '状态更新失败');
    } finally { setBusy(false); }
  };

  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{m.number}</span>
        <h3>{m.description || m.item_name_zh || '客户来料'}</h3>
        <Pill tone={customerSuppliedMaterialStatus(m.status).tone} label={customerSuppliedMaterialStatus(m.status).label} />
        {m.scenario ? <Pill tone="info" label={customerSuppliedScenario(m.scenario)} /> : null}
        <div className="spacer" />
        {loading ? <Spinner label="刷新…" /> : null}
      </div>
      {error ? <div className="notice bad">{error}</div> : null}
      {err ? <div className="notice bad" style={{ marginBottom: 8 }}>{err}</div> : null}
      {ok ? <div className="notice ok" style={{ marginBottom: 8 }}>{ok}</div> : null}
      <div className="kv-grid">
        <KeyValue k="客户">{`${m.customer_code ?? ''} ${m.customer_name ?? ''}`.trim() || m.customer_id}</KeyValue>
        <KeyValue k="关联工单">{m.work_order_number || '—'}</KeyValue>
        <KeyValue k="物料（可选）">{`${m.item_code ?? ''} ${m.item_name_zh ?? ''}`.trim() || '—（客户件非公司物料）'}</KeyValue>
        <KeyValue k="序列号">{m.serial_number || '—'}</KeyValue>
        <KeyValue k="数量">{`${num(m.quantity, 3)} ${m.uom ?? ''}`.trim()}</KeyValue>
        <KeyValue k="来料状态">{m.incoming_condition || '—'}</KeyValue>
        <KeyValue k="收到日期">{dateLabel(m.received_date)}</KeyValue>
        <KeyValue k="存放位置">{m.storage_location || '—'}</KeyValue>
        <KeyValue k="客户送货单号">{m.source_reference || '—'}</KeyValue>
        <KeyValue k="交还单">{m.return_number || '—'}</KeyValue>
        <KeyValue k="交还日期">{dateLabel(m.returned_date)}</KeyValue>
        <KeyValue k="备注">{m.notes || '—'}</KeyValue>
      </div>

      <div className="notice info" style={{ marginTop: 10, display: 'block' }}>
        <div style={{ fontSize: 12.5 }}>该来料独立于公司自有库存：不产生库存流水、不影响物料采购成本。</div>
      </div>

      {canWrite && !terminal ? (
        <div className="detail-actions" style={{ marginTop: 10 }}>
          {(NEXT_STATUS[m.status] ?? []).map((s) => (
            <button key={s.to} className="btn" disabled={busy} onClick={() => transition(s.to)}>{s.label}</button>
          ))}
          <button className="btn primary" disabled={busy} onClick={() => setReturnOpen((v) => !v)}>{returnOpen ? '收起交还单' : '开具交还单'}</button>
        </div>
      ) : null}

      {returnOpen && !terminal ? (
        <ReturnForm
          material={m}
          onCancel={() => setReturnOpen(false)}
          onCreated={(number) => { setReturnOpen(false); setOk(`已开具交还单 ${number}，来料状态已更新`); reload(); onChanged(); }}
        />
      ) : null}
    </div>
  );
}

// 针对单件来料开具交还单（数量默认全部，序列号默认为来料序列号）。
function ReturnForm({ material, onCreated, onCancel }: {
  material: CustomerSuppliedMaterial;
  onCreated: (number: string) => void;
  onCancel: () => void;
}) {
  const [outcome, setOutcome] = useState<'repaired' | 'unrepairable' | 'partial'>('repaired');
  const [returnDate, setReturnDate] = useState(today());
  const [quantity, setQuantity] = useState(String(material.quantity ?? 1));
  const [outgoing, setOutgoing] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    if (!(Number(quantity) > 0)) { setErr('交还数量需大于 0'); return; }
    setBusy(true);
    try {
      const res = await api.post<CustomerMaterialReturn>('/api/customer-supplied-materials/returns', {
        customer_id: material.customer_id,
        work_order_id: material.work_order_id || null,
        return_date: returnDate,
        handling: outcome === 'partial' ? 'partially_returned' : 'repaired',
        repair_outcome: outcome,
        lines: [{ supplied_material_id: material.id, quantity: Number(quantity), serial_number: material.serial_number || null, outgoing_condition: outgoing || null }],
      });
      onCreated(res.number);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '开具交还单失败');
    } finally { setBusy(false); }
  };

  return (
    <div className="notice info" style={{ display: 'block' }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>交还处理（归还客户 → 来料标记已交还，并回填交还单号/日期）</div>
      {err ? <div className="notice bad" style={{ marginBottom: 8 }}>{err}</div> : null}
      <FormGrid min={160}>
        <Field label="处理方式" required>
          <SelectInput value={outcome} onChange={(e) => setOutcome(e.target.value as 'repaired' | 'unrepairable' | 'partial')}>
            <option value="repaired">已修复归还</option>
            <option value="unrepairable">无法修复，原件退回</option>
            <option value="partial">部分归还</option>
          </SelectInput>
        </Field>
        <Field label="交还日期" required><DateInput value={returnDate} onChange={(e) => setReturnDate(e.target.value)} /></Field>
        <Field label={`交还数量（来料 ${num(material.quantity, 3)}）`} required><NumberInput value={quantity} onChange={(e) => setQuantity(e.target.value)} /></Field>
        <Field label="交还时状态"><TextInput value={outgoing} onChange={(e) => setOutgoing(e.target.value)} placeholder="如：功能恢复正常" /></Field>
      </FormGrid>
      <div className="detail-actions">
        <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '提交中…' : '确认交还'}</button>
        <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
      </div>
    </div>
  );
}

function NewMaterialForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerLabel, setCustomerLabel] = useState('');
  const [workOrderId, setWorkOrderId] = useState<string | null>(null);
  const [workOrderLabel, setWorkOrderLabel] = useState('');
  const [itemId, setItemId] = useState<string | null>(null);
  const [itemLabel, setItemLabel] = useState('');
  const [f, setF] = useState<Record<string, string>>({ scenario: 'repair', quantity: '1', uom: 'Unidad', received_date: today() });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string, v: string) => setF((d) => ({ ...d, [k]: v }));

  const submit = async () => {
    setErr(null);
    if (!customerId) { setErr('客户为必填'); return; }
    if (!f.received_date?.trim()) { setErr('收到日期为必填'); return; }
    setBusy(true);
    try {
      await api.post('/api/customer-supplied-materials', {
        customer_id: customerId,
        work_order_id: workOrderId || null,
        item_id: itemId || null,
        scenario: f.scenario,
        description: f.description || null,
        serial_number: f.serial_number || null,
        quantity: Number(f.quantity || 1),
        uom: f.uom || 'Unidad',
        incoming_condition: f.incoming_condition || null,
        received_date: f.received_date,
        source_reference: f.source_reference || null,
        storage_location: f.storage_location || null,
        notes: f.notes || null,
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '创建失败');
    } finally { setBusy(false); }
  };

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head"><div><h2>登记客户送修工件</h2><div className="desc">客户工件属于受托保管资产，不进入公司库存、不形成采购成本；登记客户送货单、工件身份与维修版本，后续通过交还单归还</div></div></div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
        <FormGrid>
          <Field label="客户" required>
            <SearchSelect<Customer> endpoint="/api/customers" value={customerId} label={customerLabel}
              onChange={(row) => { setCustomerId(row?.id ?? null); setCustomerLabel(row ? `${row.code} ${row.legal_name}` : ''); }}
              optionTitle={(c) => `${c.code} · ${c.legal_name}`} optionMeta={(c) => c.tax_id} placeholder="检索客户…" paginated />
          </Field>
          <Field label="关联工单（选填，客户件常挂在维修工单下）">
            <SearchSelect<WorkOrder> endpoint="/api/work-orders" value={workOrderId} label={workOrderLabel}
              onChange={(row) => { setWorkOrderId(row?.id ?? null); setWorkOrderLabel(row ? row.number : ''); }}
              optionTitle={(w) => w.number} optionMeta={(w) => `${w.item_code ?? ''} ${w.item_name_zh ?? ''}`.trim()} placeholder="检索工单…" paginated />
          </Field>
          <Field label="物料（选填，客户件可能不是公司物料）">
            <SearchSelect<Item> endpoint="/api/items" value={itemId} label={itemLabel}
              onChange={(row) => { setItemId(row?.id ?? null); setItemLabel(row ? `${row.code} ${row.name_zh}` : ''); }}
              optionTitle={(it) => `${it.code} · ${it.name_zh}`} placeholder="检索物料…" />
          </Field>
          <Field label="零件描述"><TextInput value={f.description ?? ''} onChange={(e) => set('description', e.target.value)} /></Field>
          <Field label="序列号"><TextInput value={f.serial_number ?? ''} onChange={(e) => set('serial_number', e.target.value)} placeholder="S/N …" /></Field>
          <Field label="数量" required><NumberInput value={f.quantity ?? ''} onChange={(e) => set('quantity', e.target.value)} /></Field>
          <Field label="单位"><TextInput value={f.uom ?? ''} onChange={(e) => set('uom', e.target.value)} /></Field>
          <Field label="来料状态/外观"><TextInput value={f.incoming_condition ?? ''} onChange={(e) => set('incoming_condition', e.target.value)} placeholder="如：无法开机、外观破损" /></Field>
          <Field label="收到日期" required><DateInput value={f.received_date ?? ''} onChange={(e) => set('received_date', e.target.value)} /></Field>
          <Field label="客户送货单号"><TextInput value={f.source_reference ?? ''} onChange={(e) => set('source_reference', e.target.value)} /></Field>
          <Field label="存放位置"><TextInput value={f.storage_location ?? ''} onChange={(e) => set('storage_location', e.target.value)} /></Field>
          <Field label="备注" span><TextArea value={f.notes ?? ''} onChange={(e) => set('notes', e.target.value)} /></Field>
        </FormGrid>
        <div className="detail-actions">
          <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '提交中…' : '登记来料'}</button>
          <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
        </div>
      </div>
    </section>
  );
}

// —— 交还单详情（只读：展示客户、处理方式、来料明细行）——
function ReturnDetail({ row }: { row: CustomerMaterialReturn }) {
  const { data, loading, error } = useDetail<CustomerMaterialReturn>(`/api/customer-supplied-materials/returns/${row.id}`);
  const r = data ?? row;
  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{r.number}</span>
        <h3>{r.customer_name || '客户来料交还单'}</h3>
        <Pill tone={customerMaterialReturnHandling(r.handling).tone} label={customerMaterialReturnHandling(r.handling).label} />
        <div className="spacer" />
        {loading ? <Spinner label="刷新…" /> : null}
      </div>
      {error ? <div className="notice bad">{error}</div> : null}
      <div className="kv-grid">
        <KeyValue k="客户">{`${r.customer_code ?? ''} ${r.customer_name ?? ''}`.trim() || r.customer_id}</KeyValue>
        <KeyValue k="关联工单">{r.work_order_number || '—'}</KeyValue>
        <KeyValue k="交还日期">{dateLabel(r.return_date)}</KeyValue>
        <KeyValue k="维修结论">{r.repair_outcome === 'unrepairable' ? '无法修复，原件退回' : r.repair_outcome === 'partial' ? '部分归还' : '已修复归还'}</KeyValue>
        <KeyValue k="单据状态">{r.status === 'completed' ? '已完成' : r.status === 'draft' ? '草稿' : r.status}</KeyValue>
        <KeyValue k="备注">{r.notes || '—'}</KeyValue>
      </div>
      <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '10px 0 4px' }}>交还明细</div>
      <LineTable<CustomerMaterialReturnLine>
        rows={r.lines ?? []}
        emptyText="暂无明细行"
        columns={[
          { header: '来料号', render: (l) => <span className="num">{l.material_number}</span> },
          { header: '序列号', render: (l) => l.material_serial || l.serial_number || '—' },
          { header: '数量', align: 'right', render: (l) => <span className="num">{num(l.quantity, 3)}</span> },
          { header: '交还时状态', render: (l) => l.outgoing_condition || '—' },
        ]}
      />
    </div>
  );
}

export function CustomerSuppliedMaterialsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<'materials' | 'returns'>('materials');
  const [reloadSignal, setReloadSignal] = useState(0);
  const [creating, setCreating] = useState(false);
  const canWrite = can(user, 'customer-supplied-materials', 'create');
  const bump = () => setReloadSignal((n) => n + 1);

  const materialColumns: Column<CustomerSuppliedMaterial>[] = [
    { header: '来料号', render: (m) => <span className="num strong">{m.number}</span> },
    { header: '客户', render: (m) => m.customer_name || '—' },
    { header: '场景', render: (m) => <span className="muted">{customerSuppliedScenario(m.scenario)}</span> },
    { header: '序列号', render: (m) => <span className="num muted">{m.serial_number || '—'}</span> },
    { header: '工单', render: (m) => <span className="muted">{m.work_order_number || '—'}</span> },
    { header: '数量', align: 'right', render: (m) => <span className="num">{num(m.quantity, 3)}</span> },
    { header: '收到日期', render: (m) => <span className="muted">{dateLabel(m.received_date)}</span> },
    { header: '状态', render: (m) => <Pill tone={customerSuppliedMaterialStatus(m.status).tone} label={customerSuppliedMaterialStatus(m.status).label} /> },
  ];

  const returnColumns: Column<CustomerMaterialReturn>[] = [
    { header: '交还单号', render: (r) => <span className="num strong">{r.number}</span> },
    { header: '客户', render: (r) => r.customer_name || '—' },
    { header: '工单', render: (r) => <span className="muted">{r.work_order_number || '—'}</span> },
    { header: '处理方式', render: (r) => <Pill tone={customerMaterialReturnHandling(r.handling).tone} label={customerMaterialReturnHandling(r.handling).label} /> },
    { header: '件数', align: 'right', render: (r) => <span className="num">{num(r.line_count ?? 0, 0)}</span> },
    { header: '交还日期', render: (r) => <span className="muted">{dateLabel(r.return_date)}</span> },
  ];

  return (
    <>
      <div className="tabs" style={{ display: 'flex', gap: 8, marginBottom: 'var(--gap)' }}>
        <button className={`btn${tab === 'materials' ? ' primary' : ''}`} onClick={() => { setTab('materials'); setCreating(false); }}>客户来料登记</button>
        <button className={`btn${tab === 'returns' ? ' primary' : ''}`} onClick={() => { setTab('returns'); setCreating(false); }}>交还单</button>
      </div>

      {tab === 'materials' ? (
        <>
          {creating ? (
            <NewMaterialForm onCancel={() => setCreating(false)} onCreated={() => { setCreating(false); bump(); }} />
          ) : null}
          <ListPage<CustomerSuppliedMaterial>
            title="客户送修工件"
            desc="历史/备查 · 旧「客户来料加工」记录。新收件请走「送修工件台账」（physical_assets 收件登记）；无法修复退回客户走该台账的「退料/退货单」（不进销售合同）。"
            endpoint="/api/customer-supplied-materials"
            columns={materialColumns}
            rowId={(m) => m.id}
            rowActions={(row) => <RecordActions type="customer-supplied-materials" row={row} fields={RECORD_FIELDS['customer-supplied-materials']} label={String(row.number)} onDone={() => setReloadSignal((n) => n + 1)} />}
            showYear={false}
            searchPlaceholder="搜索来料号 / 序列号 / 客户 / 工单 / 物料…"
            reloadSignal={reloadSignal}
            actions={canWrite ? (
              <button className="btn primary" onClick={() => setCreating((v) => !v)}>{creating ? '收起新建' : '登记来料'}</button>
            ) : undefined}
            renderDetail={(row) => <MaterialDetail row={row} onChanged={bump} />}
          />
        </>
      ) : (
        <ListPage<CustomerMaterialReturn>
          title="客户来料交还单"
          desc="/api/customer-supplied-materials/returns。一次归还多件来料，完成后回写来料状态与交还单号，形成「来料 → 工单 → 交还」闭环。"
          endpoint="/api/customer-supplied-materials/returns"
          columns={returnColumns}
          rowId={(r) => r.id}
          showYear={false}
          searchPlaceholder="搜索交还单号 / 客户 / 工单…"
          reloadSignal={reloadSignal}
          renderDetail={(row) => <ReturnDetail row={row} />}
        />
      )}
    </>
  );
}
